import { css, Box, styled, keyframes, StyleSystemProps } from '@withjoy/joykit';
import { NavLink } from '@react-router';
import { animationTransition } from '@shared/utils/animationTransition';
import { StyledSkeletonContentRoot } from '@shared/components/Skeleton';
import { CSSObject } from '@withjoy/joykit/components/Box/Box.types';
import { pxToRem } from '@withjoy/joykit/theme';

const fadeIn = keyframes`
  99% {
    visibility: hidden;
  }
  100% {
    visibility: visible;
  }
`;
const fadeOut = keyframes`
  99% {
    visibility: visible;
  }
  100% {
    visibility: hidden;
  }
`;

export const StyledNavRoot = styled(StyledSkeletonContentRoot)``;

export const StyledMegaMenuContainer = styled(Box).attrs({ as: 'div' })`
  position: absolute;
  background: white;
  opacity: 0;
  pointer-events: none;
  top: 100%;
  width: calc(100vw - (100vw - 100%));
  left: 0;
  transform: translateZ(0);
  box-shadow: 0px 30px 40px -20px rgb(44 41 37 / 12%), 0px 9px 36px -30px rgb(0 0 0 / 14%);
  transition: height 0.4s cubic-bezier(0.23, 1, 0.32, 1), opacity 0.4s cubic-bezier(0.38, 0.41, 0.27, 1) 0.4s;
  animation: 0.3s cubic-bezier(0.38, 0.41, 0.27, 1) 0.6s forwards ${fadeOut};
`;
export const StyledMegaMenu = styled(Box).attrs({ as: 'ul' })`
  display: grid;
  justify-content: space-between;
  width: 1656px;
  max-width: 100%;
  transform: translateZ(0);
  li {
    transform: translateY(0.425rem);
    opacity: 0;
    transition: opacity 0.2s cubic-bezier(0.38, 0.41, 0.27, 1), transform 0.3s cubic-bezier(0.395, 0, 0.135, 1);
  }
`;

export const StyledMenuPartition = styled(Box).attrs({ as: 'li' })<{ $hasContent: boolean }>`
  position: relative;
  padding-top: 32px;
  padding-bottom: 32px;
  ${props => {
    return props.$hasContent
      ? {
          ['&:not(:last-child)::after']: {
            content: '" "',
            display: 'block',
            height: 'calc(100% - 64px)',
            width: '2px',
            position: 'absolute',
            right: '24px',
            top: '50%',
            transform: 'translateY(-50%)',
            borderRight: '1px solid #ebebeb',
            pointerEvents: 'none',
          },
        }
      : null;
  }}
`;

export const StyledPartitionEntry = styled(Box).attrs({ as: 'ul' })`
  max-width: calc(100% - 48px);
`;

export const StyledPartitionCategory = styled(Box).attrs({ as: 'li' })`
  :not(:first-child) {
    margin-top: 2rem;
  }
`;

export const StyledSubCategory = styled.li``;

export const StyledPrimaryNav = styled(Box).attrs({ as: 'ul' })`
  &.nav__expanded {
    ::after {
      opacity: 1;
      width: calc(100% - 48px);
    }
  }

  :hover > li:not(:hover):not(.nav-item__active):not([data-expanded]) {
    > a {
      color: ${props => props.theme.colors.mono12};
    }
  }
  ::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;
`;

const visibleDepartmentStyle = css<{ $isVisible: boolean }>`
  > a {
    color: #000;
  }

  ${StyledMegaMenuContainer} {
    opacity: 1;
    animation: 0.15s cubic-bezier(0.38, 0.41, 0.27, 1) 0.1s forwards ${fadeIn};
    transition: height 0.4s cubic-bezier(0.23, 1, 0.32, 1), opacity 0.1s cubic-bezier(0.38, 0.41, 0.27, 1) 0.1s;
    pointer-events: all;

    li {
      opacity: 1;
      transform: translateY(0);
      transition-delay: 0.2s;
    }
  }
`;

export const StyledPrimaryNavItem = styled(Box).attrs<{ $isExpanded: boolean }>({ as: 'li' })<{ $isExpanded: boolean }>`
  ${props => {
    return props.$isExpanded ? visibleDepartmentStyle : {};
  }}
`;

export const StyledCategoryMenu = styled.ul``;

export const StyledDepartmentNavLink = styled(Box)``;
export const StyledDepartmentTitle = styled(Box)``;
export const StyledTempDepIconContainer = styled(Box)``;

export const StyledNavLink = styled(Box).attrs({ as: NavLink })<{ $isDept: boolean }>`
  ${props => {
    return props.$isDept
      ? {
          whiteSpace: 'nowrap',
          '&.active': {
            color: props.theme.colors.linkHover,
          },
        }
      : {};
  }}
`;

const primaryNavStyles: StyleSystemProps = {
  display: 'inline-flex',
  justifyContent: 'center',
  paddingTop: [pxToRem(8), null, pxToRem(12)],
  paddingX: 6,
  columnGap: pxToRem(24),
  marginX: [null, null, null, null, 'auto'],
  width: '100%',
  _after: {
    content: "' '",
    display: 'block',
    height: '1px',
    backgroundColor: 'mono3',
    position: 'absolute',
    opacity: 0,
    bottom: 0,
    left: ' 50%',
    transform: 'translateX(-50%)',
    width: 'calc(100% - 96px)',
    pointerEvents: 'none',
    transition: animationTransition('opacity', 'width'),
  },
};

const primaryNavItemStyles: CSSObject = {
  height: '100%',
  '& > a svg': {
    transition: animationTransition('color'),
  },
};

const departmentNavLinkStyles: StyleSystemProps = {
  display: 'flex',
  height: '100%',
  flexDirection: 'column',
  alignItems: 'center',
  typographyVariant: 'hed1',
  fontWeight: 'medium',
  whiteSpace: 'nowrap',
  color: 'mono12',
  _hover: {
    color: 'linkHover',

    [` ${StyledDepartmentTitle}`]: {
      _after: {
        backgroundColor: 'linkHover',
      },
    },
  },
  _focus: {
    color: 'linkHover',
  },
  _active: {
    color: '#000',
    [` ${StyledDepartmentTitle}`]: {
      _after: {
        backgroundColor: '#000',
      },
    },
  },
  _activeLink: {
    color: '#000 !important',
    [` ${StyledDepartmentTitle}`]: {
      _after: {
        backgroundColor: '#000',
        height: '2px',
      },
    },
  },
};

const departmentTitleStyles: StyleSystemProps = {
  position: 'relative',
  display: 'inline-block',
  paddingBottom: [pxToRem(4), null, pxToRem(12)],
  paddingTop: [pxToRem(12), null, 0],
  transition: animationTransition('color'),
  fontSize: '17px',
  fontWeight: 500,
  lineHeight: 1.2,
  _notFirstChild: {
    marginTop: 3,
  },
  _after: {
    display: 'block',
    content: "' '",
    position: 'absolute',
    bottom: '2px',
    left: 0,
    right: 0,
    height: '2px',
    borderRadius: 'full',
    backgroundColor: 'transparent',
    transition: animationTransition('background-color', 'height'),
  },
};

const catalogSearchBarStyles: StyleSystemProps = {
  display: 'flex',
  justifyContent: 'flex-end',
  flex: [0, 0, 0, 0, 1],
};

const tempDepIconContainerStyles: CSSObject = {
  flexGrow: 1,
  '[data-svgtrans="stroke"]': {
    stroke: 'currentColor',
  },
  '[data-svgtrans="fill"]': {
    fill: 'currentColor',
  },
};

export const styles = {
  catalogSearchBarStyles,
  departmentNavLink: departmentNavLinkStyles,
  departmentTitle: departmentTitleStyles,
  primaryNav: primaryNavStyles,
  primaryNavItem: primaryNavItemStyles,
  tempDepIconContainer: tempDepIconContainerStyles,
};
